import { forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';
import { clsx } from 'clsx';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  showPasswordStrength?: boolean;
  passwordStrength?: 'weak' | 'fair' | 'good' | 'strong';
}

/**
 * Input component based on V4 Step 1 authentication design
 * Includes label, error states, helper text, and password strength indicator
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = true,
      className,
      id,
      showPasswordStrength = false,
      passwordStrength,
      ...props
    },
    ref
  ) => {
    // Generate unique ID if not provided
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

    // Input styles matching V4 Step 1 design
    const inputStyles = [
      'px-4',
      'py-3',
      'border',
      'rounded-lg',
      'bg-[var(--auth-form-input-bg)]',
      'text-[var(--auth-text-primary)]',
      'text-sm',
      'transition-all',
      'duration-200',
      fullWidth && 'w-full',
      error
        ? 'border-red-500 focus:border-red-500'
        : 'border-[var(--auth-form-input-border)] focus:border-[var(--auth-form-input-border-focus)]',
      'focus:outline-none',
      'focus:ring-0',
      'focus:shadow-[var(--auth-form-input-shadow-focus)]',
      'placeholder:text-[var(--auth-text-secondary)]',
      'disabled:opacity-50',
      'disabled:cursor-not-allowed',
    ];

    // Password strength styles
    const strengthStyles = {
      weak: 'w-1/4 bg-red-500',
      fair: 'w-1/2 bg-orange-500',
      good: 'w-3/4 bg-yellow-500',
      strong: 'w-full bg-green-500',
    };

    const strengthMessages = {
      weak: 'Too weak - add more variety',
      fair: 'Fair - add more character types',
      good: 'Good - almost there!',
      strong: 'Strong password!',
    };

    return (
      <div className={clsx('space-y-1.5', className)}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-[var(--auth-text-primary)]"
          >
            {label}
          </label>
        )}

        <input
          ref={ref}
          id={inputId}
          className={clsx(inputStyles)}
          {...props}
        />

        {showPasswordStrength && passwordStrength && (
          <div className="space-y-1">
            <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={clsx(
                  'h-full transition-all duration-300 rounded-full',
                  strengthStyles[passwordStrength]
                )}
              />
            </div>
            <p className="text-xs text-[var(--auth-text-secondary)]">
              {strengthMessages[passwordStrength]}
            </p>
          </div>
        )}

        {error && (
          <p className="text-xs text-red-500 mt-1">{error}</p>
        )}

        {helperText && !error && (
          <p className="text-xs text-[var(--auth-text-secondary)] mt-1">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
